import React, { useMemo, useState } from "react";

/**
 * Contact (EmailJS-ready)
 * Matches the EmailJS template variables you set up:
 *  - name
 *  - email
 *  - phone
 *  - message
 *  - submitted_at   (e.g., "Wed, 05 Nov 2025 17:21 (Europe/London)")
 *  - reply_to       (same as email, used by EmailJS for Reply-To)
 * You can also keep a honeypot field `website` to block bots.
 */
const Contact = () => {
  const [formData, setFormData] = useState({
    email: "",
    name: "",
    phone: "",
    message: "",
    website: "", // honeypot (must remain empty)
  });

  const [status, setStatus] = useState({ state: "idle", msg: "" }); // idle | sending | success | error
  const [errors, setErrors] = useState({});

  // Format timestamp in Europe/London to match your email template
  const submittedAt = useMemo(() => {
    try {
      const dt = new Date();
      const date = dt.toLocaleString("en-GB", {
        weekday: "short",
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
        timeZone: "Europe/London",
      });
      return `${date} (Europe/London)`;
    } catch {
      return new Date().toISOString();
    }
  }, [status.state]); // recompute on send cycle if you want fresh timestamp

  const validate = () => {
    const e = {};
    if (!formData.name.trim()) e.name = "Please enter your name.";
    if (!formData.email.trim()) {
      e.email = "Please enter your email.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i.test(formData.email.trim())) {
      e.email = "Please enter a valid email address.";
    }
    if (!formData.message.trim()) e.message = "Please enter a message.";
    if (formData.website) e.website = "Spam detected.";
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const v = validate();
    setErrors(v);
    if (Object.keys(v).length > 0) {
      setStatus({ state: "error", msg: "Please fix the highlighted fields." });
      return;
    }

    try {
      setStatus({ state: "sending", msg: "" });

      // === EmailJS REST payload ===
      // Ensure these IDs match your EmailJS dashboard configuration.
      const apiUrl = "https://api.emailjs.com/api/v1.0/email/send";
      const credentials = {
        service_id: "service_tc9pwjg",
        template_id: "template_ujd5als",
        user_id: "8mJgKdWP4KMilGRz_",
        template_params: {
          // Required by your template:
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          message: formData.message,
          submitted_at: submittedAt,
          // Helpful aliases for forwarding/replying:
          reply_to: formData.email,
          from_name: formData.name,
          from_email: formData.email,
        },
      };

      const res = await fetch(apiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(credentials),
      });

      if (!res.ok) throw new Error("Network response was not ok.");

      // Some EmailJS services return empty body; ignore parse errors.
      await res.json().catch(() => null);

      setStatus({
        state: "success",
        msg: "Message sent successfully. Thank you!",
      });
      setFormData({ email: "", name: "", phone: "", message: "", website: "" });
    } catch (err) {
      console.error(err);
      setStatus({
        state: "error",
        msg: "We couldn’t send your message right now. Please try again shortly.",
      });
    }
  };

  const isSending = status.state === "sending";

  return (
    <section
      id="contact"
      className="relative min-h-screen bg-[#FFE8BA] overflow-hidden"
    >
      {/* warm vignette to match other sections */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(60%_60%_at_100%_0%,rgba(245,158,11,0.12),transparent_60%),radial-gradient(60%_60%_at_0%_100%,rgba(234,88,12,0.12),transparent_65%)]" />

      <div className="mx-auto w-full max-w-7xl px-5 sm:px-6 md:px-10 lg:px-12 py-10 md:py-16">
        {/* Header pill */}
        <h1 className="text-center text-neutral-900 text-[32px] md:text-[40px] font-extrabold tracking-tight bg-white/70 backdrop-blur ring-1 ring-amber-200 shadow-sm rounded-2xl px-5 py-3">
          Contact
        </h1>

        <div className="mt-8 md:mt-10 flex justify-center">
          <form
            onSubmit={handleSubmit}
            className="w-full md:w-[640px] rounded-3xl bg-white/85 backdrop-blur-sm ring-1 ring-amber-200 shadow-xl px-6 md:px-8 py-6 md:py-8"
            noValidate
          >
            {/* Honeypot (hidden) */}
            <input
              type="text"
              name="website"
              value={formData.website}
              onChange={(e) =>
                setFormData((s) => ({ ...s, website: e.target.value }))
              }
              className="hidden"
              tabIndex={-1}
              autoComplete="off"
            />

            {/* Name */}
            <div className="mb-5">
              <label
                htmlFor="name"
                className="block text-[16px] md:text-[18px] font-medium text-neutral-800"
              >
                Name
              </label>
              <input
                id="name"
                type="text"
                placeholder="Enter full name"
                value={formData.name}
                onChange={(e) =>
                  setFormData((s) => ({ ...s, name: e.target.value }))
                }
                aria-invalid={!!errors.name}
                aria-describedby={errors.name ? "name-error" : undefined}
                className={`mt-2 w-full rounded-xl border p-3 text-[16px] shadow-sm focus:outline-none focus:ring-2 transition ${
                  errors.name
                    ? "border-rose-400 focus:ring-rose-300/60"
                    : "border-amber-200 focus:border-amber-400 focus:ring-amber-300/60"
                }`}
              />
              {errors.name && (
                <p id="name-error" className="mt-2 text-sm text-rose-600">
                  {errors.name}
                </p>
              )}
            </div>

            {/* Split: Phone + Email */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
              <div>
                <label
                  htmlFor="phone"
                  className="block text-[16px] md:text-[18px] font-medium text-neutral-800"
                >
                  Phone Number
                </label>
                <input
                  id="phone"
                  type="tel"
                  placeholder="Enter phone number"
                  value={formData.phone}
                  onChange={(e) =>
                    setFormData((s) => ({ ...s, phone: e.target.value }))
                  }
                  className="mt-2 w-full rounded-xl border border-amber-200 p-3 text-[16px] shadow-sm focus:outline-none focus:ring-2 focus:border-amber-400 focus:ring-amber-300/60 transition"
                />
              </div>

              <div>
                <label
                  htmlFor="email"
                  className="block text-[16px] md:text-[18px] font-medium text-neutral-800"
                >
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  placeholder="Enter email address"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData((s) => ({ ...s, email: e.target.value }))
                  }
                  aria-invalid={!!errors.email}
                  aria-describedby={errors.email ? "email-error" : undefined}
                  className={`mt-2 w-full rounded-xl border p-3 text-[16px] shadow-sm focus:outline-none focus:ring-2 transition ${
                    errors.email
                      ? "border-rose-400 focus:ring-rose-300/60"
                      : "border-amber-200 focus:border-amber-400 focus:ring-amber-300/60"
                  }`}
                />
                {errors.email && (
                  <p id="email-error" className="mt-2 text-sm text-rose-600">
                    {errors.email}
                  </p>
                )}
              </div>
            </div>

            {/* Message */}
            <div className="mb-6">
              <label
                htmlFor="message"
                className="block text-[16px] md:text-[18px] font-medium text-neutral-800"
              >
                Message
              </label>
              <textarea
                id="message"
                rows={6}
                placeholder="Write your message…"
                value={formData.message}
                onChange={(e) =>
                  setFormData((s) => ({ ...s, message: e.target.value }))
                }
                aria-invalid={!!errors.message}
                aria-describedby={errors.message ? "message-error" : undefined}
                className={`mt-2 w-full rounded-xl border p-3 text-[16px] shadow-sm focus:outline-none focus:ring-2 transition resize-y ${
                  errors.message
                    ? "border-rose-400 focus:ring-rose-300/60"
                    : "border-amber-200 focus:border-amber-400 focus:ring-amber-300/60"
                }`}
              />
              {errors.message && (
                <p id="message-error" className="mt-2 text-sm text-rose-600">
                  {errors.message}
                </p>
              )}
            </div>

            {/* Status banner */}
            {status.state !== "idle" && status.msg && (
              <div
                className={`mb-4 rounded-xl px-4 py-3 text-sm font-medium ${
                  status.state === "success"
                    ? "bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200"
                    : status.state === "error"
                    ? "bg-rose-50 text-rose-800 ring-1 ring-rose-200"
                    : "bg-amber-50 text-amber-900 ring-1 ring-amber-200"
                }`}
                role="status"
              >
                {status.msg}
              </div>
            )}

            <button
              type="submit"
              disabled={isSending}
              className={`w-full inline-flex items-center justify-center gap-2 rounded-xl py-4 text-[18px] font-semibold text-white shadow-md transition ${
                isSending
                  ? "bg-amber-400 cursor-not-allowed"
                  : "bg-gradient-to-r from-amber-500 via-orange-500 to-amber-600 hover:opacity-90"
              }`}
            >
              {isSending ? (
                <>
                  <svg
                    className="h-5 w-5 animate-spin"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                    />
                  </svg>
                  Sending…
                </>
              ) : (
                <>Send Message</>
              )}
            </button>

            <p className="mt-3 text-center text-xs text-neutral-500">
              We’ll never share your details. By submitting, you agree to be contacted about your enquiry.
            </p>
          </form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
